import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header2";
import FilterPanel from "./components/FilterPanel";
import FilterChips from "./components/FilterChips";
import ProductListHeader from "./components/ProductListHeader";
import ProductGrid from "./components/ProductGrid";
import Icon from "../../components/AppIcon";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllProductsService,
  getCategoriesService,
  toggleFavService,
} from "redux/services/general_service";
import { useTranslation } from "react-i18next";
import { setFilterData } from "redux/slices/general";

const ProductListing = () => {
  const { t } = useTranslation();

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { metaProducts, productsList, isLoadingProduct, filtersData } =
    useSelector((state) => state.general);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalProducts, setTotalProducts] = useState(0);
  const [viewMode, setViewMode] = useState("grid");
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [currentSort, setCurrentSort] = useState("relevance");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [filters, setFilters] = useState({
    /*category: [],
    customPrice: { price_start: "", price_end: "" },*/
  });
  let hasParams = false;
  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const newProducts = await dispatch(
        getAllProductsService({ page: nextPage, ...filtersData })
      );

      console.log(newProducts?.payload?.data?.data);
      // Assuming newProducts is an array of products returned from the API
      setProducts((prevProducts) => [
        ...prevProducts,
        ...newProducts?.payload?.data?.data,
      ]);
      setCurrentPage(nextPage);
    } catch (error) {
      console.error("Error loading more products:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleAddToCart = async (product, variant = null) => {
    // Simulate add to cart
    return new Promise((resolve) => {
      setTimeout(() => {
        const cartCount =
          parseInt(localStorage.getItem("cartCount") || "0") + 1;
        localStorage.setItem("cartCount", cartCount.toString());

        // Dispatch event for cart update
        window.dispatchEvent(
          new CustomEvent("cartUpdated", {
            detail: { count: cartCount },
          })
        );

        resolve();
      }, 500);
    });
  };

  const handleToggleWishlist = (productId) => {
    setProducts((prev) =>
      prev.map((product) =>
        product.id === productId
          ? { ...product, isWishlisted: !product.isWishlisted }
          : product
      )
    );
    dispatch(toggleFavService({ product_id: productId }));
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    const params = new URLSearchParams(location.search);
    if (newFilters.category.length > 0) {
      params.set("category", newFilters.category[0]);
    } else {
      params.delete("category");
    }

    const newUrl = `${location.pathname}${
      params.toString() ? "?" + params.toString() : ""
    }`;
    navigate(newUrl, { replace: true });
  };

  const handleRemoveFilter = (filterType, filterValue) => {
    const newFilters = { ...filters };

    if (
      filterType === "category" ||
      filterType === "brand" ||
      filterType === "features"
    ) {
      newFilters[filterType] = newFilters[filterType].filter(
        (item) => item !== filterValue
      );
    } else if (filterType === "priceRange") {
      newFilters.priceRange = null;
    } else if (filterType === "customPrice") {
      newFilters.customPrice = { price_start: "", price_end: "" };
    } else if (filterType === "rating") {
      newFilters.rating = null;
    }

    setFilters(newFilters);
  };

  const handleClearAllFilters = () => {
    const clearedFilters = {
      category: [],
      brand: [],
      priceRange: null,
      customPrice: { price_start: "", price_end: "" },
      rating: null,
      features: [],
    };
    setFilters(clearedFilters);
  };

  const handleSortChange = (newSort) => {
    setCurrentSort(newSort);

    // Update URL
    const params = new URLSearchParams(location.search);
    if (newSort !== "relevance") {
      params.set("sort", newSort);
    } else {
      params.delete("sort");
    }

    const newUrl = `${location.pathname}${
      params.toString() ? "?" + params.toString() : ""
    }`;
    navigate(newUrl, { replace: true });
  };

  // Parse URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get("featured")) {
      dispatch(setFilterData({ is_special: 1 }));
      setFilters((prev) => ({
        ...prev,
        is_special: 1,
      }));
    } else if (params.get("discounted")) {
      dispatch(setFilterData({ has_discount: 1 }));
      setFilters((prev) => ({
        ...prev,
        has_discount: 1,
      }));
    } else if (params.get("categories")) {
      //     filtersFromParams.category = [category];
      hasParams = true;
    }

    const search = params.get("search") || "";
    const category = params.get("category") || "";
    const sort = params.get("sort") || "relevance";

    setSearchQuery(search);
    setSelectedCategory(category);
    setCurrentSort(sort);

    // Update filters based on URL
    if (category) {
      setFilters((prev) => ({
        ...prev,
        category: [category],
      }));
    }
  }, [location.search]);

  // Load products when filters change
  useEffect(() => {
    console.log("Filters:", filters);
    setProducts([]);

    // Fetch products with updated filters
    if (!hasParams || filtersData === null) {
      dispatch(getAllProductsService({ page: 1, ...filters })).then(
        (response) => {
          if (response?.payload?.status == 200) {
            setProducts(response?.payload?.data?.data);
          }
        }
      );
    }
  }, [filters]);

  // Load categories on component mount
  useEffect(() => {
    dispatch(getCategoriesService({ page: 1 }));
  }, []);

  // Set hasMore based on meta information
  useEffect(() => {
    if (metaProducts) {
      setHasMore(metaProducts.current_page < metaProducts.last_page);
    }
  }, [metaProducts]);

  // Check if productsList is not empty and products is still empty
  useEffect(() => {
    if (productsList.length > 0 && products.length === 0) {
      setProducts(productsList);
    }
  }, [productsList, products]);

  const handleSearch = (val) => {
    console.log(val);
    dispatch(getAllProductsService({ search: val })).then((response) => {
      if (response?.payload?.status == 200) {
        setProducts(response?.payload?.data?.data);
      }
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="flex">
        {/* Desktop Filter Sidebar */}
        <div className="hidden lg:block">
          <FilterPanel
            isOpen={true}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            isMobile={false}
            setProducts={setProducts}
            setCurrentPage={setCurrentPage}
          />
        </div>

        {/* Mobile Filter Panel */}
        <FilterPanel
          isOpen={isFilterPanelOpen}
          onClose={() => setIsFilterPanelOpen(false)}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          isMobile={true}
          setProducts={setProducts}
          setCurrentPage={setCurrentPage}
        />

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Filter Chips */}
          <FilterChips
            filters={filters}
            onRemoveFilter={handleRemoveFilter}
            onClearAll={handleClearAllFilters}
            setProducts={setProducts}
          />

          {/* Product List Header */}
          <ProductListHeader
            totalProducts={metaProducts?.total}
            currentSort={currentSort}
            onSortChange={handleSortChange}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onOpenFilters={() => setIsFilterPanelOpen(true)}
            searchQuery={searchQuery}
            selectedCategory={selectedCategory}
            loading={loading}
            handleSearch={handleSearch}
          />

          {/* Product Grid */}
          <div className="p-4 ">
            {
              <ProductGrid
                products={products}
                loading={isLoadingProduct}
                hasMore={hasMore}
                onLoadMore={handleLoadMore}
                onAddToCart={handleAddToCart}
                onToggleWishlist={handleToggleWishlist}
                viewMode={viewMode}
                total={metaProducts?.total}
              />
            }
          </div>
        </div>
      </div>

      {/* Pull to Refresh Indicator - Mobile Only */}
      <div className="lg:hidden fixed bottom-20 right-4 z-40">
        <button
          disabled={loading}
          className="w-12 h-12 bg-primary text-white rounded-full shadow-interactive flex items-center justify-center hover:bg-primary/90 transition-smooth disabled:opacity-50"
          aria-label={t("refreshProducts")} // Translated refresh button label
        >
          <Icon
            name="RotateCcw"
            size={20}
            className={loading ? "animate-spin" : ""}
          />
        </button>
      </div>
    </div>
  );
};

export default ProductListing;
